import {
  DatabaseReference,
  DataSnapshot,
  endAt,
  limitToLast,
  onDisconnect,
  onValue,
  orderByChild,
  push,
  query,
  ref,
  serverTimestamp,
  set,
  update
} from 'firebase/database';
import { auth, getRealtimeDb } from './firebase';

const requireDatabase = () => {
  const dbInstance = getRealtimeDb();
  if (!dbInstance) {
    throw new Error('Realtime Database is not configured. Add databaseURL to firebase-applet-config.json.');
  }
  return dbInstance;
};

const requireUser = () => {
  if (!auth.currentUser) throw new Error('Authentication is required for realtime features.');
  return auth.currentUser;
};

export interface RealtimeMessage {
  id: string;
  senderUid: string;
  text: string;
  createdAt: number;
}

export const conversationIdFor = (firstUid: string, secondUid: string) =>
  [firstUid, secondUid].sort().join('_');

export const sendChatMessage = async (
  conversationId: string,
  recipientUid: string,
  text: string
): Promise<string> => {
  const user = requireUser();
  const cleanText = text.trim();
  if (!cleanText) throw new Error('Message cannot be empty.');

  const database = requireDatabase();
  await set(ref(database, `chats/${conversationId}/participants/${user.uid}`), true);
  const messageRef = push(ref(database, `chats/${conversationId}/messages`));
  await set(messageRef, {
    senderUid: user.uid,
    recipientUid,
    text: cleanText.slice(0, 2000),
    createdAt: serverTimestamp()
  });
  await update(ref(database, `chats/${conversationId}/meta`), {
    lastMessage: cleanText.slice(0, 200),
    lastMessageAt: serverTimestamp(),
    lastSenderUid: user.uid
  });
  return messageRef.key ?? '';
};

export const joinChatConversation = async (conversationId: string): Promise<void> => {
  const user = requireUser();
  await set(ref(requireDatabase(), `chats/${conversationId}/participants/${user.uid}`), true);
};

export const subscribeToChat = (
  conversationId: string,
  callback: (messages: RealtimeMessage[]) => void
) => {
  const user = requireUser();
  const messagesRef = query(
    ref(requireDatabase(), `chats/${conversationId}/messages`),
    orderByChild('createdAt'),
    limitToLast(100)
  );
  return onValue(messagesRef, snapshot => {
    const messages: RealtimeMessage[] = [];
    snapshot.forEach(child => {
      const value = child.val() as Omit<RealtimeMessage, 'id'>;
      if (value.senderUid === user.uid || value.senderUid) {
        messages.push({ id: child.key ?? '', ...value });
      }
    });
    callback(messages);
  });
};

export const setPresence = async (isOnline: boolean): Promise<void> => {
  const user = requireUser();
  const presenceRef = ref(requireDatabase(), `presence/${user.uid}`);
  await onDisconnect(presenceRef).set({
    online: false,
    lastSeen: serverTimestamp()
  });
  await set(presenceRef, {
    online: isOnline,
    lastSeen: serverTimestamp()
  });
};

export const subscribeToPresence = (
  uid: string,
  callback: (online: boolean) => void
) => onValue(ref(requireDatabase(), `presence/${uid}`), snapshot => {
  callback(Boolean(snapshot.val()?.online));
});

export const publishLiveState = async (
  liveId: string,
  state: { isLive: boolean; viewerCount: number; title?: string }
) => {
  const user = requireUser();
  await set(ref(requireDatabase(), `live/${liveId}`), {
    ...state,
    ownerUid: user.uid,
    updatedAt: serverTimestamp()
  });
};

export const subscribeToLiveState = (
  liveId: string,
  callback: (snapshot: DataSnapshot) => void
) => onValue(ref(requireDatabase(), `live/${liveId}`), callback);

export const getChatReference = (conversationId: string): DatabaseReference =>
  ref(requireDatabase(), `chats/${conversationId}`);
